// The workspace the app is working in (backend/index.js: captures, labels,
// capture favorites and share links belong to a workspace + splat; the
// Dashboard lists the workspace's splats). Picked in the settings menu
// (components/WorkspaceMenu.js); remembered per browser; a ?ws= in the URL
// (Scene / Captures links) wins for that page. Checked once per page load
// against the caller's own workspaces (myWorkspaces), so a stale or foreign
// id is never sent: sysAdmins default to the built-in Admin workspace,
// everyone else to their first one. authFetch (apiHelper.js) adds the
// result as ?ws= to every API call — except on share links, where the
// share itself decides.
//
// Uses fetch directly (not authFetch) — authFetch waits on this.
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';

const STORAGE_KEY = 'cinesplat-workspace';
export const ADMIN_WORKSPACE = 'admin';

const readStored = () => { try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; } };
const store = (id) => { try { if (id) localStorage.setItem(STORAGE_KEY, id); else localStorage.removeItem(STORAGE_KEY); } catch (e) { /* storage off */ } };

let ready = null;      // Promise<{ workspaceId, workspaces }>
let resolved = null;   // its value once settled

async function load() {
  if (sessionStorage.getItem('shareToken')) return { workspaceId: null, workspaces: [] };
  let idToken = null;
  try { idToken = (await fetchAuthSession()).tokens?.idToken?.toString() || null; } catch (e) { /* logged out */ }
  if (!idToken) return { workspaceId: null, workspaces: [] };
  let workspaces = [];
  try {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ myWorkspaces: true }),
    });
    if (res.ok) workspaces = (await res.json()).workspaces || [];
  } catch (e) { /* offline: no workspace param, backend uses the default */ }
  const fromUrl = new URLSearchParams(window.location.search).get('ws');
  const has = (id) => !!id && workspaces.some((w) => w.workspaceId === id);
  const workspaceId = has(fromUrl) ? fromUrl : has(readStored()) ? readStored() : (workspaces[0]?.workspaceId || null);
  store(workspaceId);
  return { workspaceId, workspaces };
}

/** Resolves once per page load: { workspaceId, workspaces: [{ workspaceId, name }] }. */
export function workspaceReady() {
  if (!ready) ready = load().then((v) => { resolved = v; window.dispatchEvent(new CustomEvent('workspaceReady', { detail: v })); return v; });
  return ready;
}

/**
 * Re-read the caller's workspaces (after Manage creates / renames /
 * duplicates / deletes one, or changes membership) so the settings menu
 * updates without a reload. Keeps the current workspace when it still
 * exists; returns the new { workspaceId, workspaces }.
 */
export function refreshWorkspaces() {
  ready = load().then((v) => { resolved = v; window.dispatchEvent(new CustomEvent('workspaceReady', { detail: v })); return v; });
  return ready;
}

/** The checked current workspace id (null before workspaceReady() settles or when logged out). */
export const currentWorkspaceId = () => resolved?.workspaceId || null;

/** Switch workspace: remembered, then back to the Dashboard (its splats). */
export function switchWorkspace(id) {
  store(id);
  window.location.href = '/';
}

/** Forget the choice (logout). */
export function clearWorkspace() {
  store(null);
  ready = null;
  resolved = null;
}
